import React, { useContext, useEffect, useState } from "react";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import {
  Box,
  Button,
  Checkbox,
  Fab,
  IconButton,
  Link,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { useNavigate } from "react-router-dom";
import { ArrowBack, ArrowForward, Send } from "@mui/icons-material";
import { SnackAlertContext } from "../../../context/SnackAlertContext";
import { AuthContext } from "../../../context/AuthContext";
import { toThreeDecimals } from "../../../components/NewDetail/calculations/utils";
import PrintWeeklyTimeSummariesButton from "./PrintWeeklyTimeSummariesButton";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc"; // ES 2015
import timezone from "dayjs/plugin/timezone"; // ES 2015
import isoWeek from "dayjs/plugin/isoWeek"; // ES 2015
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isoWeek);
const DEFAULT_TIMEZONE = "America/Phoenix";
dayjs.tz.setDefault(DEFAULT_TIMEZONE);

// isoDate is "YYYY-MM-DD"
// the isoDateParam will always be treated as a Phoenix Date
const WeeklyTimeSummaries = ({ isoDateParam }) => {
  // dayjs(param) will parse the param in UTC!!!. To parse the date as a Phoenix date then we need to uyse dayjs.tz([datestring], timezone)

  const getPhoenixDayJs = (isoDate) => {
    return isoDate
      ? dayjs.tz(isoDate, DEFAULT_TIMEZONE).startOf("day") // Treat "YYYY-MM-DD" passed in as a Phoenix Arizona Date
      : dayjs().tz(DEFAULT_TIMEZONE).startOf("day"); // Convert current time to arizona time
  };

  // So if you were reading this in a different country which is 1 day ahead, it will load that "YYYY-MM-DD"
  // as if it was "YYYY-MM-DD" in Arizona
  const navigate = useNavigate();
  const { ezorder, authToken } = useContext(AuthContext);
  const { openSnackMessage } = useContext(SnackAlertContext);
  const [loading, setLoading] = useState(false);
  const [weeklyUserTimeSummaries, setWeeklyUserTimeSummaries] = useState([]);

  const setDate = (dateUpdate) => {
    let query = [];
    if (dateUpdate) {
      query.push(`date=${dateUpdate}`);
    }
    if (query.length > 0) {
      navigate(`/weekly-time-summaries?${query.join("&")}`); // search=hello&status=DRAFT
    } else {
      navigate(`/weekly-time-summaries`);
    }
  };

  // TODO: Read this
  // https://day.js.org/docs/en/plugin/timezone
  const getWeeklyUserTimeSummariesByPhoenixIsoDate = async () => {
    try {
      setLoading(true);
      // Monday
      let phoenixIsoDate = getPhoenixDayJs(isoDateParam)
        .tz(DEFAULT_TIMEZONE)
        .startOf("isoWeek")
        .format("YYYY-MM-DD");

      let response = await ezorder.get(
        `/weekly-user-time-summaries/${phoenixIsoDate}`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (response.status == 200 || response.status === 201) {
        setWeeklyUserTimeSummaries(response.data.weeklyUserTimeSummaries);
      }
    } catch (error) {
      console.log(error);
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.log(error.response.data);
        console.log(error.response.status);
        console.log(error.response.headers);
        openSnackMessage("error", error.response.data.error);
      } else {
        // Something went really bad
        console.log(error);
        openSnackMessage("error", error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    try {
      setLoading(true);
      let selectedTimeSummaries = [];
      for (let weeklyUserTimeSummary of weeklyUserTimeSummaries) {
        if (weeklyUserTimeSummary.checked) {
          selectedTimeSummaries.push(weeklyUserTimeSummary);
        }
      }

      let response = await ezorder.patch(
        `/approve-weekly-user-time-summaries`,
        { weeklyUserTimeSummaries: selectedTimeSummaries },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (response.status == 200 || response.status === 201) {
        // navigate(`/time-entries`);
        openSnackMessage("success", response.data.message);
        getWeeklyUserTimeSummariesByPhoenixIsoDate(); // refresh available user list
      } else {
        openSnackMessage("error", response.response.data.error);
      }
    } catch (error) {
      console.log(error);
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.log(error.response.data);
        console.log(error.response.status);
        console.log(error.response.headers);
        openSnackMessage("error", error.response.data.error);
      } else {
        // Something went really bad
        console.log(error);
        openSnackMessage("error", error);
      }
    } finally {
      setLoading(false);
    }
  };

  const resend = async (weeklyUserTimeSummaryId) => {
    try {
      setLoading(true);
      let response = await ezorder.get(
        `/admin/user/time-summaries/${weeklyUserTimeSummaryId}/resend`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (response.status == 200 || response.status === 201) {
        // navigate(`/time-entries`);
        openSnackMessage("success", response.data.message);
        getWeeklyUserTimeSummariesByPhoenixIsoDate(); // refresh available user list
      } else {
        openSnackMessage("error", response.response.data.error);
      }
    } catch (error) {
      console.log(error);
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.log(error.response.data);
        console.log(error.response.status);
        console.log(error.response.headers);
        openSnackMessage("error", error.response.data.error);
      } else {
        // Something went really bad
        console.log(error);
        openSnackMessage("error", error);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getWeeklyUserTimeSummariesByPhoenixIsoDate();
  }, [isoDateParam]);

  useEffect(() => {
    console.log(weeklyUserTimeSummaries);
  }, [weeklyUserTimeSummaries]);

  return (
    <Box>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <IconButton
            color="primary"
            onClick={() => {
              let current = getPhoenixDayJs(isoDateParam)
                .tz(DEFAULT_TIMEZONE)
                .startOf("isoWeek");
              let prev = current.subtract(1, "week");
              setDate(prev.tz(DEFAULT_TIMEZONE).format("YYYY-MM-DD"));
            }}
          >
            <ArrowBack />
          </IconButton>
          <DesktopDatePicker
            shouldDisableDate={(date) => {
              return date.tz(DEFAULT_TIMEZONE).day() != 1; // Monday Only
            }}
            inputFormat="ddd MM/DD/YY" // display format
            label="Week Of (Phoenix Arizona)"
            // BY DEFAULT, DAYJS.format() will format in local time and not UTC
            // So the YYYY-MM-DD we're parsing out will be accurate
            value={getPhoenixDayJs(isoDateParam).startOf("isoWeek")}
            onChange={(newValue) => {
              setDate(
                newValue
                  .tz(DEFAULT_TIMEZONE)
                  .startOf("isoWeek")
                  .format("YYYY-MM-DD")
              );
            }}
            renderInput={(params) => (
              <TextField size="small" sx={{ width: "200px" }} {...params} />
            )}
          />
          <IconButton
            color="primary"
            onClick={() => {
              let current = getPhoenixDayJs(isoDateParam)
                .tz(DEFAULT_TIMEZONE)
                .startOf("isoWeek");
              let next = current.add(1, "week");
              setDate(next.tz(DEFAULT_TIMEZONE).format("YYYY-MM-DD"));
            }}
          >
            <ArrowForward />
          </IconButton>
        </Box>
        <TableContainer component={Box}>
          <Table sx={{}}>
            <TableHead>
              <TableRow>
                <TableCell align="left" sx={{ width: "70px" }}></TableCell>
                <TableCell align="left">Employee</TableCell>
                <TableCell align="left">Week Total Hours</TableCell>
                <TableCell align="left">No-Work Days</TableCell>
                <TableCell align="left">Admin Approved</TableCell>
                <TableCell align="left">Last Sent</TableCell>
                <TableCell align="left">Employee Signed</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {weeklyUserTimeSummaries &&
                weeklyUserTimeSummaries.map((weeklyUserTimeSummary, index) => {
                  return (
                    <TableRow key={weeklyUserTimeSummary.id}>
                      <TableCell align="left">
                        <Box>
                          <Checkbox
                            disabled={weeklyUserTimeSummary.adminApprovedOn}
                            style={{
                              magin: 0,
                              padding: 0,
                            }}
                            checked={
                              // If already approved by admin, checked
                              weeklyUserTimeSummary.adminApprovedOn ||
                              weeklyUserTimeSummary.checked
                            }
                            onChange={(e) => {
                              let weeklyUserTimeSummariesCopy = [
                                ...weeklyUserTimeSummaries,
                              ];
                              weeklyUserTimeSummariesCopy[index].checked =
                                e.target.checked;
                              setWeeklyUserTimeSummaries(
                                weeklyUserTimeSummariesCopy
                              );
                            }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell
                        align="left"
                        onClick={() => {
                          navigate(
                            `/weekly-time-summaries/${weeklyUserTimeSummary.id}`
                          );
                        }}
                      >
                        <Box
                          sx={{
                            width: "300px",
                            textTransform: "uppercase",
                            fontWeight: "bold",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: "1",
                            WebkitBoxOrient: "vertical",
                          }}
                        >
                          {weeklyUserTimeSummary.user.fullName}
                        </Box>
                      </TableCell>
                      <TableCell
                        align="left"
                        onClick={() => {
                          navigate(
                            `/weekly-time-summaries/${weeklyUserTimeSummary.id}`
                          );
                        }}
                      >
                        {toThreeDecimals(
                          Number(weeklyUserTimeSummary.totalWorkMinutes ?? 0) /
                            60
                        )}
                      </TableCell>
                      <TableCell
                        align="left"
                        onClick={() => {
                          navigate(
                            `/weekly-time-summaries/${weeklyUserTimeSummary.id}`
                          );
                        }}
                      >
                        {Number(weeklyUserTimeSummary.totalNoWorkDays ?? 0)}
                      </TableCell>
                      <TableCell
                        align="left"
                        onClick={() => {
                          navigate(
                            `/weekly-time-summaries/${weeklyUserTimeSummary.id}`
                          );
                        }}
                      >
                        {weeklyUserTimeSummary.adminApprovedOn
                          ? `${dayjs(weeklyUserTimeSummary.adminApprovedOn)
                              .tz(DEFAULT_TIMEZONE)
                              .format("MM/DD/YY")} by ${
                              weeklyUserTimeSummary.adminApprovedBy.fullName
                            }`
                          : ""}
                      </TableCell>
                      <TableCell
                        align="left"
                        onClick={() => {
                          // navigate(
                          //   `/weekly-time-summaries/${weeklyUserTimeSummary.id}`
                          // );
                        }}
                      >
                        {weeklyUserTimeSummary.adminApprovedOn && (
                          <>
                            <IconButton
                              onClick={() => {
                                resend(weeklyUserTimeSummary.id);
                              }}
                            >
                              <Send />
                            </IconButton>
                            {weeklyUserTimeSummary.textNotificationLastSent
                              ? `${dayjs(
                                  weeklyUserTimeSummary.textNotificationLastSent
                                )
                                  .tz(DEFAULT_TIMEZONE)
                                  .format("MM/DD/YY hh:mm a")}`
                              : "Not Sent"}
                          </>
                        )}
                      </TableCell>
                      <TableCell
                        align="left"
                        onClick={() => {
                          navigate(
                            `/weekly-time-summaries/${weeklyUserTimeSummary.id}`
                          );
                        }}
                      >
                        {weeklyUserTimeSummary.employeeSignedOn
                          ? `${dayjs(weeklyUserTimeSummary.employeeSignedOn)
                              .tz(DEFAULT_TIMEZONE)
                              .format("MM/DD/YY")}`
                          : ""}
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      </LocalizationProvider>

      <div
        style={{
          position: "fixed",
          bottom: "20px",
          right: "50px",
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        <Fab
          disabled={loading}
          onClick={handleApprove}
          variant="extended"
          color="success"
        >
          Approve & Send Signing Links
        </Fab>
        {/* <Fab
          disabled={loading}
          onClick={() => {}}
          variant="extended"
          color="success"
        >
          Print Summary
        </Fab> */}
        <PrintWeeklyTimeSummariesButton
          start={getPhoenixDayJs(isoDateParam)
            .tz(DEFAULT_TIMEZONE)
            .startOf("isoWeek")}
          weeklyUserTimeSummaries={weeklyUserTimeSummaries}
          disabled={loading}
        />
      </div>
    </Box>
  );
};

export default WeeklyTimeSummaries;
